// @file For all whiteboard global state and logic
import { trackEvent } from '@@/bits/analytics'
import { isTouchScreen } from '@@/bits/browser'
import { ANXIOUS_FACE_EMOJI } from '@@/bits/emoji'
import { captureFetchException } from '@@/bits/error_tracker'
import { __ } from '@@/bits/intl'
import { reload } from '@@/bits/location'
import { getVuexStore } from '@@/bits/pinia'
import { tryCatch } from '@@/bits/try_catch'
import type { TldrawFontId } from '@@/bits/whiteboard'
import { FRAME_ID_QUERY_PARAM, getTldrawFontId, MAX_FRAME_LIMIT } from '@@/bits/whiteboard'
import { VIEWER_TOOL_IDS } from '@@/bits/whiteboard_permissions'
import { getValidAttachmentShapesForFrame, isValidAttachmentShape } from '@@/bits/whiteboard_shape_checks'
import type { CONNECTOR_COLORS, TldrawDefaultColorStyleColor } from '@@/bits/whiteboard_tools'
import { arrowToolColors, DEFAULT_COLORS, geoToolColors, getAllTools, isPadletTool } from '@@/bits/whiteboard_tools'
import { OzConfirmationDialogBoxButtonScheme } from '@@/library/v4/components/OzConfirmationDialogBox.vue'
import { useGlobalAlertDialogStore } from '@@/pinia/global_alert_dialog'
import { useGlobalConfirmationDialogStore } from '@@/pinia/global_confirmation_dialog'
import { SnackbarNotificationType, useGlobalSnackbarStore } from '@@/pinia/global_snackbar'
import { UpgradeSource, UpgradeStep, usePersonalPlansStore } from '@@/pinia/personal_plans_store'
import { useSurfaceStore } from '@@/pinia/surface'
import { useSurfaceContainerSizeStore } from '@@/pinia/surface_container_size'
import { useSurfacePostsStore } from '@@/pinia/surface_posts'
import { useWhiteboardCardTemplatesStore } from '@@/pinia/whiteboard_card_templates'
import { useWhiteboardFramePreviewStore } from '@@/pinia/whiteboard_frame_preview'
import { useWhiteboardModeStore } from '@@/pinia/whiteboard_mode'
import { useWhiteboardPermissionsStore } from '@@/pinia/whiteboard_permissions'
import { useWhiteboardToolsStore } from '@@/pinia/whiteboard_tools'
import { WishApi } from '@@/surface/api/wish'
import type { Frame, WhiteboardSecondaryMenu, WhiteboardTool } from '@@/types/whiteboard'
import { useCopyToClipboard } from '@@/vuecomposables/copy_to_clipboard'
import { useClipboardItems } from '@@/vuecomposables/useClipboardItems'
import { useTldrawEditor } from '@@/vuecomposables/useTldrawEditor'
import { useWhiteboardFrameActions } from '@@/vuecomposables/use_whiteboard_frame_actions'
import { useWhiteboardToolActions } from '@@/vuecomposables/whiteboard_tool_actions'
import type { RootState } from '@@/vuexstore/surface/types'
import type { PadletAttachmentShape } from '@padlet/whiteboard-schema'
import { defineStore } from 'pinia'
import type { TLContent, TLPageId, TLShape, TLShapeId } from 'tldraw'
import { getIndexBetween, PageRecordType } from 'tldraw'
import { computed, ref } from 'vue'

export const useWhiteboardStore = defineStore('whiteboard', () => {
  const whiteboardCardTemplatesStore = useWhiteboardCardTemplatesStore()
  const whiteboardFramePreviewStore = useWhiteboardFramePreviewStore()
  const whiteboardPermissionStore = useWhiteboardPermissionsStore()
  const whiteboardModeStore = useWhiteboardModeStore()
  const surfaceStore = useSurfaceStore()
  const { copyToClipboard } = useCopyToClipboard()
  const globalSnackbarStore = useGlobalSnackbarStore()
  const personalPlansStore = usePersonalPlansStore()
  const globalAlertDialogStore = useGlobalAlertDialogStore()
  const globalConfirmationDialogStore = useGlobalConfirmationDialogStore()
  const surfacePostsStore = useSurfacePostsStore()
  const whiteboardToolsStore = useWhiteboardToolsStore()
  const whiteboardFrameActions = useWhiteboardFrameActions()

  /**
   * SHAPE APPROVAL
   */
  const unapprovedShapes = ref<TLShape[]>([])
  const numUnapprovedShapes = computed(() => {
    return unapprovedShapes.value.length
  })
  const timesUnapprovedShapesUpdated = ref(0)

  /** FRAMES */
  // #region FRAMES
  const frames = ref<Frame[]>([])
  const currentFrameId = ref<TLPageId | null>(null)
  const previousFrameId = ref<TLPageId | null>(null)
  const xFrameBackgroundPanel = ref(false)
  const xFrameSharePanel = ref(false)
  const xFrameShareViaAnotherAppPanel = ref(false)
  const xFrameBreakoutLinksPanel = ref(false)
  const frameBarHeight = ref(0)

  useTldrawEditor().react((editor) => {
    if (currentFrameId.value !== editor.getCurrentPageId()) {
      // When switching between modes, this react is run and the currentFrameId is updated again to be the same
      // Guard it so that it doesn't set previousFrameId to be the same as currentFrameId
      previousFrameId.value = currentFrameId.value
      currentFrameId.value = editor.getCurrentPageId()

      // For some reason, if we call this method the first time `currentFrameId` is populated, it causes a bug
      // where a visitor can't add shape after they load the sandbox until they change to a different tool and
      // change it back again. I don't understand how that's the case because we also have other watchers that
      // call this method. For now, let's just block it from running the first time by checking that `previousFrameId`
      // is already non-null (the first time, `previousFrameId` is null).
      if (previousFrameId.value != null) {
        useWhiteboardToolActions().selectFirstAllowedTool()
      }
    }
  })

  const backgroundDominantColorInRgba = computed(() => {
    const opacity = 0.2
    const dominantColor = surfaceStore.background?.dominant_color
    if (dominantColor == null || dominantColor.length === 0) {
      return surfaceStore.backgroundLuminance === 'dark'
        ? `rgba(14, 13, 9, ${opacity})`
        : `rgba(229, 219, 202, ${opacity})`
    }
    return `rgba(${dominantColor.join(',')}, ${opacity})`
  })
  const frameBarAndToolbarBackground = computed(() => {
    return surfaceStore.backgroundLuminance === 'dark'
      ? `linear-gradient(0deg, ${backgroundDominantColorInRgba.value} 0%, ${backgroundDominantColorInRgba.value} 100%), linear-gradient(0deg, rgba(255, 255, 255, 0.10) 0%, rgba(255, 255, 255, 0.10) 100%), rgba(0, 0, 0, 0.70)`
      : `linear-gradient(0deg, ${backgroundDominantColorInRgba.value} 0%, ${backgroundDominantColorInRgba.value} 100%), linear-gradient(0deg, rgba(0, 0, 0, 0.05) 0%, rgba(0, 0, 0, 0.05) 100%), rgba(255, 255, 255, 0.70)`
  })
  const toolbarMenuBackground = computed(() => {
    return surfaceStore.backgroundLuminance === 'dark'
      ? `linear-gradient(0deg, ${backgroundDominantColorInRgba.value} 0%, ${backgroundDominantColorInRgba.value} 100%), linear-gradient(0deg, rgba(255, 255, 255, 0.10) 0%, rgba(255, 255, 255, 0.10) 100%), rgba(0, 0, 0, 0.75)`
      : `linear-gradient(0deg, ${backgroundDominantColorInRgba.value} 0%, ${backgroundDominantColorInRgba.value} 100%), linear-gradient(0deg, rgba(0, 0, 0, 0.05) 0%, rgba(0, 0, 0, 0.05) 100%), rgba(255, 255, 255, 0.75)`
  })

  function setFrameBarHeight(height: number): void {
    frameBarHeight.value = height
  }

  // This is used for background/share panel since the current frame isn't always the frame that the user opened a panel for
  const panelFrameId = ref<TLPageId>()

  const getFrameIndex = (frameId: string): number => frames.value.findIndex((frame) => frame.id === frameId)

  const isSingleFrame = computed(() => frames.value.length === 1)
  function isFirstFrame(frameId: string): boolean {
    return frames.value[0]?.id === frameId
  }
  function isLastFrame(frameId: string): boolean {
    return frames.value[frames.value.length - 1]?.id === frameId
  }

  function getCanAddFrame(): boolean {
    if (!whiteboardPermissionStore.canIManage || surfaceStore.isFrozen) {
      return false
    }
    if (hasReachedPlanFrameLimitAndCannotUpgrade.value) {
      trackEvent('Whiteboard', 'Cannot add frame because of plan limit')
      globalAlertDialogStore.openAlertDialog({
        iconSrc: ANXIOUS_FACE_EMOJI.iconSrc,
        title: __('Card limit reached'),
        body: __('This sandbox has a limit of %{frameLimit} cards.', {
          frameLimit: surfaceStore.whiteboardFrameLimit,
        }),
        xShadow: true,
      })
      return false
    }
    if (shouldShowUpgradeModal.value) {
      trackEvent('Whiteboard', 'Shown upgrade modal after trying to add frame')
      if (surfaceStore.isLibraryWall) {
        // TODO: For future handling of libraries
        // This code block won't execute for now because of hasReachedPlanFrameLimitAndCannotUpgrade
      } else {
        personalPlansStore.userTriggeredUpgrade({
          user: surfaceStore.user,
          upgradeSource: UpgradeSource.WhiteboardAddFrameClick,
          step: UpgradeStep.ChooseTierFrameQuota,
        })
      }

      return false
    }

    return true
  }

  function addFrameIfPossible(frameIndex: number): void {
    const canAddFrame = getCanAddFrame()
    if (canAddFrame) {
      whiteboardFrameActions.addFrame(frameIndex)
      // Select appropriate tool after adding frame:
      // - Text tool for desktop/web users for better text input experience
      // - Drawing tool for mobile/touch devices (including Chromebooks) for natural drawing input
      selectTool(isTouchScreen() ? 'draw-nested' : 'text')

      // After adding frame, suggest card templates
      whiteboardCardTemplatesStore.xSuggestedCardTemplatesRow = true
      whiteboardCardTemplatesStore.xStartFromATemplateButton = true
    }
  }

  function addFrameAbove(frameId: string): void {
    const frameIndex = getFrameIndex(frameId)
    // We supply the same index because when adding a frame above, we want the new frame to take the index of the current frame
    addFrameIfPossible(frameIndex)
  }
  function addFrameBelow(frameId: string): void {
    const frameIndex = getFrameIndex(frameId)
    addFrameIfPossible(frameIndex + 1)
  }

  function moveFrameUp(frameId: TLPageId): void {
    const indexOfFrameToMove = getFrameIndex(frameId)

    whiteboardFrameActions.moveFrame(frameId, indexOfFrameToMove, indexOfFrameToMove - 1)
  }
  function moveFrameDown(frameId: TLPageId): void {
    const indexOfFrameToMove = getFrameIndex(frameId)

    whiteboardFrameActions.moveFrame(frameId, indexOfFrameToMove, indexOfFrameToMove + 1)
  }

  function moveFrameAbove({ frameId, targetFrameId }: { frameId: TLPageId; targetFrameId: TLPageId }): void {
    const indexOfFrameToMove = getFrameIndex(frameId)
    const indexOfTargetFrame = getFrameIndex(targetFrameId)

    if (indexOfFrameToMove < indexOfTargetFrame) {
      whiteboardFrameActions.moveFrame(frameId, indexOfFrameToMove, indexOfTargetFrame - 1)
    }

    if (indexOfFrameToMove > indexOfTargetFrame) {
      whiteboardFrameActions.moveFrame(frameId, indexOfFrameToMove, indexOfTargetFrame)
    }
  }

  function moveFrameBelow({ frameId, targetFrameId }: { frameId: TLPageId; targetFrameId: TLPageId }): void {
    const indexOfFrameToMove = getFrameIndex(frameId)
    const indexOfTargetFrame = getFrameIndex(targetFrameId)

    if (indexOfFrameToMove < indexOfTargetFrame) {
      whiteboardFrameActions.moveFrame(frameId, indexOfFrameToMove, indexOfTargetFrame)
    }

    if (indexOfFrameToMove > indexOfTargetFrame) {
      whiteboardFrameActions.moveFrame(frameId, indexOfFrameToMove, indexOfTargetFrame + 1)
    }
  }

  /**
   * Deletes a frame from the whiteboard after user confirmation
   * @param frameId - The ID of the frame to delete
   * @param context - Analytics context object passed to trackEvent to identify where the deletion was triggered from
   *                  (e.g., { context: 'Context menu' }, { context: 'Keyboard' }, etc.)
   */
  function deleteFrame(
    frameId: TLPageId,
    options: { context: 'Context menu' | 'Keyboard' | 'Unknown' } = {
      context: 'Unknown',
    },
  ): void {
    if (isSingleFrame.value) {
      globalAlertDialogStore.openAlertDialog({
        title: __('Card cannot be deleted'),
        body: __('At least one card is required. Please create a new card if you want to delete this one.'),
        closeButtonText: __('OK'),
        shouldFadeIn: true,
      })
      return
    }

    globalConfirmationDialogStore.openConfirmationDialog({
      title: __('Delete this card?'),
      body: __('Are you sure you want to delete this card? This cannot be undone!'),
      confirmButtonText: __('Delete'),
      cancelButtonText: __('Nevermind'),
      buttonScheme: OzConfirmationDialogBoxButtonScheme.Danger,
      afterConfirmActions: [
        () => {
          trackEvent('Whiteboard', 'Deleted frame', null, null, options)
          whiteboardFrameActions.deleteFrame(frameId)
        },
      ],
    })
  }

  interface DuplicateFrameHelperParams {
    sourceFrameId: TLPageId
    targetFrameId: TLPageId | null
    errorMessage: string
    historyMessage: string
  }

  async function duplicateFrameHelper({
    sourceFrameId,
    targetFrameId,
    errorMessage,
    historyMessage,
  }: DuplicateFrameHelperParams): Promise<void> {
    const editor = useTldrawEditor().editor
    if (editor == null) return
    // We copy how `duplicatePage` is implemented in Tldraw so have more control over the process.
    // See `tldraw#Editor.duplicatePage`

    if (editor.getPages().length >= editor.options.maxPages) return

    const sourcePage = editor.getPage(sourceFrameId)
    if (sourcePage == null) return

    const prevCamera = { ...editor.getCamera() }
    const content = editor.getContentFromCurrentPage(editor.getSortedChildIdsForParent(sourcePage.id))
    let finalContent: TLContent
    if (content != null) {
      finalContent = {
        ...content,
        shapes: content.shapes.map((shape) => ({
          ...shape,
          meta: {
            ...shape.meta,
            // Set this so in `whiteboard_shape_handlers.ts#registerAfterCreateHandler('shape')`
            // we can handle the shapes differently. Basically we want to retain the shapes
            // that were created by the now-disabled tools. Without this flag, the shape handler
            // callback will delete those shapes.
            isFromDuplicatedFrame: true,
          },
        })),
      }
    }

    const attachmentShapes = getValidAttachmentShapesForFrame(sourceFrameId)
    let postIdMapping: Record<string, number> = {}

    // If we are unable to copy ANY attachment shape, for now, we do not continue.
    if (attachmentShapes.length > 0) {
      const deepCopyShapesResult = await deepCopyShapes(
        attachmentShapes.map((shape) => shape.id),
        errorMessage,
      )
      if (deepCopyShapesResult === false) {
        return
      }

      postIdMapping = deepCopyShapesResult
    }

    const newPageId = PageRecordType.createId()

    editor.markHistoryStoppingPoint(historyMessage)
    editor.run(() => {
      const pages = editor.getPages()
      // Find the target position based on our current frame
      const currentPageIndex = pages.findIndex((page) => page.id === (targetFrameId ?? sourceFrameId))

      // Calculate index for insertion after current page
      const nextPage = pages[currentPageIndex + 1]
      const index = getIndexBetween(pages[currentPageIndex]?.index, nextPage?.index)

      // Copy over the meta too.
      editor.createPage({ name: sourcePage.name + ' Copy', id: newPageId, index, meta: sourcePage.meta })
      editor.setCurrentPage(newPageId)
      editor.setCamera(prevCamera)

      // Immediately copy the frame preview from source to avoid blank preview lag
      whiteboardFramePreviewStore.copyFramePreview({ sourceFrameId, targetFrameId: newPageId })

      if (finalContent != null) {
        // If we had content on the previous page, put it on the new page
        editor.putContentOntoCurrentPage(finalContent)
      }

      const newPage = editor.getCurrentPage()
      const newPageAttachmentShapes = getValidAttachmentShapesForFrame(newPage.id)
      if (attachmentShapes.length > 0 && newPageAttachmentShapes.length > 0) {
        updateCopiedShapes(postIdMapping, newPageAttachmentShapes)
      }
    })
  }

  async function duplicateFrame(frameId: TLPageId): Promise<void> {
    await duplicateFrameHelper({
      sourceFrameId: frameId,
      targetFrameId: frameId,
      errorMessage: __('Failed to duplicate frame'),
      historyMessage: 'duplicate frame',
    })
  }

  async function copyFrame(frameId: TLPageId): Promise<boolean> {
    const { writeClipboardFrameId } = useClipboardItems()
    const result = await writeClipboardFrameId(frameId)
    return result
  }

  /**
   * Pastes a frame by duplicating it from the source frame ID.
   *
   * @param sourceFrameId - The source frame ID to copy from
   * @param targetFrameId - Optional target frame to paste after (defaults to current frame)
   * @returns Promise<boolean> - true if successful, false if failed
   */
  async function pasteFrame(sourceFrameId: TLPageId, targetFrameId?: TLPageId): Promise<boolean> {
    // Use the provided targetFrameId, or fall back to currentFrameId
    const frameIdToPasteAfter = targetFrameId ?? currentFrameId.value
    if (frameIdToPasteAfter == null) {
      return false
    }

    if (!getCanAddFrame()) {
      return false
    }

    await duplicateFrameHelper({
      sourceFrameId,
      targetFrameId: frameIdToPasteAfter,
      errorMessage: __('Failed to paste frame'),
      historyMessage: 'paste frame',
    })

    return true
  }

  // Deep copy shapes with their associated data (polls, images, etc.)
  const deepCopyShapes = async (
    shapeIds: TLShapeId[],
    errorMessage: string = __('Failed to duplicate attachment'),
  ): Promise<Record<string, number> | false> => {
    const editor = useTldrawEditor().editor
    if (editor == null) return false

    if (shapeIds.length === 0) {
      return false
    }

    const shapes = shapeIds.map((id) => editor.getShape(id)).filter((shape): shape is TLShape => shape != null)
    if (shapes.length === 0) {
      return false
    }

    const attachmentShapes = shapes.filter((shape): shape is PadletAttachmentShape => isValidAttachmentShape(shape))

    if (attachmentShapes.length === 0) {
      return {}
    }

    // Attempt to copy attachment posts with error handling and snackbar notifications
    const attemptCopyAttachmentPosts = async (
      attachmentShapes: PadletAttachmentShape[],
      errorMessage: string = __('Failed to duplicate attachment'),
    ): Promise<Record<string, number> | null> => {
      if (attachmentShapes.length === 0) {
        return {}
      }

      try {
        // For same-sandbox copying, we have the posts in the store
        const copyPromises = attachmentShapes.map(async (attachmentShape) => {
          const postId = attachmentShape.props.postId
          const post = surfacePostsStore.postEntitiesById[postId]
          const wallHashId = surfaceStore.wallHashid

          if (!post?.hashid) {
            throw new Error(`Post or hashid not found for postId: ${postId}`)
          }

          let response
          if (attachmentShape.props.isPoll) {
            // Use poll-specific copy method for polls
            response = await WishApi.copyWhiteboardPoll({
              wishHashid: post.hashid,
              wallHashid: wallHashId,
            })
          } else {
            // Use general copy method for images and other attachments
            response = await WishApi.copy({
              wishHashid: post.hashid,
              wallHashid: wallHashId,
              transferCustomPropValuesAcrossWalls: false,
            })
          }

          const newPostId = Number(response.data.id)
          return { originalPostId: postId, newPostId }
        })

        const copyResults = await Promise.allSettled(copyPromises)
        const postIdMapping: Record<string, number> = {}

        copyResults.forEach((result, index) => {
          if (result.status === 'fulfilled') {
            const { originalPostId, newPostId } = result.value
            postIdMapping[originalPostId] = newPostId
          } else {
            const failedShape = attachmentShapes[index]
            captureFetchException(result.reason, {
              context: 'Failed to copy whiteboard attachment during duplication',
              shapeId: failedShape.id,
              postId: failedShape.props.postId,
              isPoll: failedShape.props.isPoll,
            })
          }
        })

        return postIdMapping
      } catch (error) {
        captureFetchException(error)
        globalSnackbarStore.setSnackbar({
          message: errorMessage,
          notificationType: SnackbarNotificationType.error,
        })
        throw error
      }
    }

    const [postIdMappingResult, error] = await tryCatch(
      async () => await attemptCopyAttachmentPosts(attachmentShapes, errorMessage),
    )

    if (error != null || postIdMappingResult === null) {
      return false
    }

    return postIdMappingResult
  }

  // Update copied shapes with the postId mapping and refresh posts
  const updateCopiedShapes = (postIdMapping: Record<string, number>, copiedShapes: TLShape[]): void => {
    const editor = useTldrawEditor().editor
    if (editor == null) return

    // Update the postId of the duplicated attachment shapes to the copied posts
    const copiedAttachmentShapes = copiedShapes.filter((shape): shape is PadletAttachmentShape =>
      isValidAttachmentShape(shape),
    )

    const shapesToUpdate = copiedAttachmentShapes.map((shape) => {
      const originalPostId = shape.props.postId
      const newPostId = postIdMapping[originalPostId]

      if (newPostId) {
        return {
          ...shape,
          props: {
            ...shape.props,
            postId: newPostId,
          },
        }
      }
      return null
    })

    const validShapesToUpdate = shapesToUpdate.filter((shape) => shape !== null)

    if (validShapesToUpdate.length > 0) {
      editor.updateShapes(validShapesToUpdate)
    }

    // Refresh posts to show the new duplicated attachments
    if (Object.keys(postIdMapping).length > 0) {
      surfacePostsStore.fetchPosts()
    }
  }

  // #endregion FRAMES

  /**
   * FRAME LIMITS
   * */

  const hasReachedPlanFrameLimit = computed(() => frames.value.length >= surfaceStore.whiteboardFrameLimit)
  const hasReachedMaximumFrameLimit = computed(() => frames.value.length >= MAX_FRAME_LIMIT)
  const canUpgradeToIncreaseFrameLimit = computed<boolean>(() => {
    if (hasReachedMaximumFrameLimit.value) {
      return false
    }
    if (surfaceStore.isLibraryWall) {
      // TODO: Support upgrade for library trial
      return false
    }
    return surfaceStore.isBuilderCurrentUser
  })
  const shouldShowUpgradeModal = computed(() => hasReachedPlanFrameLimit.value && canUpgradeToIncreaseFrameLimit.value)
  const hasReachedPlanFrameLimitAndCannotUpgrade = computed<boolean>(
    () => hasReachedPlanFrameLimit.value && !canUpgradeToIncreaseFrameLimit.value,
  )

  /**
   * FRAME URLS
   */
  // #region FRAME URLS
  const getLinkToFrame = (frameId: TLPageId): string => {
    const linkToFrame = new URL(surfaceStore.links.show)
    linkToFrame.searchParams.append(FRAME_ID_QUERY_PARAM, frameId)

    return linkToFrame.toString()
  }

  const copyLinkToFrame = (frameId: TLPageId): void => {
    void copyToClipboard({
      text: getLinkToFrame(frameId),
      globalSnackbarOptions: {
        message: __('Link copied to clipboard'),
        notificationType: SnackbarNotificationType.success,
        timeout: 1500,
      },
    })
  }

  const copyFrameBreakoutLink = (frameId: TLPageId): void => {
    const frameBreakoutLink = frameBreakoutLinks.value[frameId]
    if (frameBreakoutLink === '') {
      globalSnackbarStore.setSnackbar({
        message: __('There was an error creating the breakout room. Please try again'),
        notificationType: SnackbarNotificationType.error,
      })
      return
    }

    void copyToClipboard({
      text: frameBreakoutLink,
      globalSnackbarOptions: {
        message: __('Link copied to clipboard'),
        notificationType: SnackbarNotificationType.success,
        timeout: 1500,
      },
    })
  }

  // #endregion FRAME URLS

  /** FRAME BREAKOUT LINKS */
  // #region FRAME BREAKOUT LINKS
  // We use wallHashid and not public key because we don't want users to be able to go back to the original wall.
  // Format: {origin}/{namespace}/card-breakout-room/{wallHashidWithoutPrefix}-{frameId}
  // E.g. padlet.com/username/card-breakout-room/wallHashidWithoutPrefix-frameId
  const frameBreakoutOriginPrefix = computed(() => {
    const namespace: string = surfaceStore.namespace
    const origin = new URL(window.location.href).origin
    return `${origin}/${namespace}`
  })
  const frameBreakoutLinkPrefix = computed(() => `${frameBreakoutOriginPrefix.value}/card-breakout-link`)
  const frameBreakoutRoomPrefix = computed(() => `${frameBreakoutOriginPrefix.value}/card-breakout-room`)
  // Support backwards compatibility with old card-breakout-link in the url
  const isFrameBreakout = computed(() => {
    const frameBreakoutPrefixes = [frameBreakoutLinkPrefix.value, frameBreakoutRoomPrefix.value]
    return frameBreakoutPrefixes.some((prefix) => window.location.href.includes(prefix))
  })

  /**
   * Format: {origin}/{namespace}/card-breakout-room/{wallHashidWithoutPrefix}-{breakoutFrameId}
   * Use this value to get the current breakout room's frame ID instead of `currentFrameId`
   * if you need it before Tldraw renders.
   */
  const breakoutFrameId = computed<TLPageId | null>(() => {
    const currentUrlWithoutQueryParams = window.location.origin + window.location.pathname
    if (!isFrameBreakout.value) return null
    const frameBreakoutPrefixes = [frameBreakoutLinkPrefix.value, frameBreakoutRoomPrefix.value]
    const prefix = frameBreakoutPrefixes.find((prefix) => currentUrlWithoutQueryParams.includes(prefix))
    if (prefix == null) return null
    const identifier = currentUrlWithoutQueryParams.split(prefix)[1]
    const frameId = identifier?.split(`/${surfaceStore.wallHashidWithoutPrefix}-`).pop()
    if (frameId == null) return null
    return frameId as TLPageId
  })

  const frameBreakoutLinks = computed<{ [frameId: TLPageId]: string }>(() => {
    // We don't want any _ in the url
    // Example format of wallHashid: 'board_mockRandomHash'
    const wallHashidWithoutPrefix: string = surfaceStore.wallHashidWithoutPrefix
    const frameIdToBreakoutLinkMapping: { [frameId: string]: string } = {}

    frames.value.forEach((frame) => {
      frameIdToBreakoutLinkMapping[frame.id] = `${frameBreakoutRoomPrefix.value}/${wallHashidWithoutPrefix}-${frame.id}`
    })
    return frameIdToBreakoutLinkMapping
  })

  useTldrawEditor().react((editor) => {
    if (breakoutFrameId.value == null) return
    const allFrameIds = Array.from(editor.getPages()).map((page) => page.id)
    if (allFrameIds.length === 0) return
    if (!allFrameIds.includes(breakoutFrameId.value)) {
      // Reload to show the error page in SurfaceController#check_breakout_room_availability
      reload()
    }
  })

  // #endregion FRAME BREAKOUT LINKS

  /** TOOLS & SELECTED TOOL */
  // #region TOOLS & SELECTED TOOL

  const tools = ref<WhiteboardTool[]>(
    getAllTools().map((tool) => ({
      ...tool,
    })),
  )

  const toolStates = ref<
    Record<string, { currentSecondarySelectionId?: string; fillStyle?: 'none' | 'semi' | 'solid' }>
  >(
    getAllTools().reduce(
      (acc, tool) => ({ ...acc, [tool.id]: { currentSecondarySelectionId: tool.secondaryMenu?.defaultSelection } }),
      {},
    ),
  )

  const toolCurrentColors = computed<Record<string, string | undefined>>(() => {
    const result: Record<string, string | undefined> = {}

    Object.keys(toolStates.value).forEach((toolId) => {
      const toolState = toolStates.value[toolId]
      if (toolId === 'draw-nested' && toolState.currentSecondarySelectionId != null) {
        result[toolId] = toolStates.value[toolState.currentSecondarySelectionId].currentSecondarySelectionId
        return
      }
      if (toolId === 'geo') {
        result[toolId] = geoColorMenuSelectionId.value
        return
      }

      if (toolId === 'arrow') {
        result[toolId] = arrowColorMenuSelectionId.value
        return
      }

      if (toolState.currentSecondarySelectionId != null) {
        result[toolId] = toolState.currentSecondarySelectionId
      }
    })

    return result
  })

  const isFirstTimeSelectingTool = ref(true)
  const selectedToolId = ref<string>('select')

  const selectedTool = computed(() => {
    return tools.value.find((tool) => tool.id === selectedToolId.value)
  })

  function selectTool(toolId: string): void {
    if (currentFrameId.value == null) return
    if (toolId === selectedToolId.value) return
    // tldraw tools
    if (!isPadletTool(toolId) && !whiteboardPermissionStore.isToolAllowed(toolId, currentFrameId.value)) return
    // Padlet tools
    if (isPadletTool(toolId) && !whiteboardToolsStore.allEnabledToolIds.includes(toolId)) return

    selectedToolId.value = toolId
    // Dismiss suggested card templates row when selecting a tool.
    // Only do it for subsequent times, not the first time (because we call `selectTool`
    // after Tldraw is mounted).
    if (!isFirstTimeSelectingTool.value) {
      whiteboardCardTemplatesStore.dismissCardTemplatesSuggestionUI()
    }

    isFirstTimeSelectingTool.value = false
  }

  function selectDefaultTool(): void {
    const tldrawEditor = useTldrawEditor().editor
    if (tldrawEditor == null) {
      return
    }

    const isEmptyCanvas = tldrawEditor.getCurrentPageShapeIds().size === 0
    const isWriteableEmptyCanvas =
      whiteboardPermissionStore.canIWriteFrame(tldrawEditor.getCurrentPageId()) && isEmptyCanvas

    let defaultToolId: string

    if (whiteboardModeStore.isReadOnlyMode) {
      defaultToolId = VIEWER_TOOL_IDS[0]
    } else if (isWriteableEmptyCanvas && isTouchScreen()) {
      defaultToolId = 'draw-nested'
    } else {
      defaultToolId = 'select'
    }

    selectTool(defaultToolId)
  }

  /**
   * Sometimes we want to change the selected tool without triggering the tldraw tool change
   * This is useful when we want to change the tool but not the tldraw tool
   * Find usage by searching for `selectToolWithoutTriggeringTldraw`
   */
  const ignoreTldrawTrigger = ref(false)
  function selectToolWithoutTriggeringTldraw(toolId: string): void {
    selectedToolId.value = toolId
    ignoreTldrawTrigger.value = true
  }

  function selectSecondaryTool(secondaryToolId: string): void {
    // Check if the selected tool has a secondary menu and the selected secondary tool is valid
    if (selectedTool.value?.secondaryMenu?.items == null) return
    if (!selectedTool.value.secondaryMenu.items.some((item) => item.id === secondaryToolId)) return

    toolStates.value = {
      ...toolStates.value,
      [selectedToolId.value]: { currentSecondarySelectionId: secondaryToolId },
    }
  }

  // Selected color or shape tool
  const selectedSecondaryTool = computed(() => {
    return selectedTool.value?.secondaryMenu?.items.find(
      (item) => item.id === toolStates.value?.[selectedToolId.value]?.currentSecondarySelectionId,
    )
  })

  // const selectedTertiaryTool = computed(() => {
  //   return selectedSecondaryTool.value?.secondaryMenu?.items.find(
  //     (item) => item.id === selectedSecondaryTool.value?.state.currentSecondarySelectionId,
  //   )
  // })

  // #endregion TOOLS & SELECTED TOOL

  /** GEOMETRY COLOR TOOLBAR */
  // #region GEOMETRY COLOR TOOLBAR
  // Geo color Menu
  const geoColorMenu = ref<WhiteboardSecondaryMenu | null>({
    id: 'color',
    items: geoToolColors,
    defaultSelection: DEFAULT_COLORS.shapeBorder,
  })

  const geoColorMenuSelectionId = ref<string>(DEFAULT_COLORS.shapeBorder)

  function selectGeoColor(colorId: string): void {
    if (geoColorMenu.value?.items.some((item) => item.id === colorId) == null) return
    geoColorMenuSelectionId.value = colorId
  }

  const geoColorTldrawToolId = computed<TldrawDefaultColorStyleColor | undefined>(
    () =>
      geoColorMenu.value?.items.find((item) => item.id === geoColorMenuSelectionId.value)?.tldrawToolId as
        | TldrawDefaultColorStyleColor
        | undefined,
  )

  // #endregion GEOMETRY COLOR TOOLBAR

  /** ARROW COLOR TOOLBAR */
  // #region ARROW COLOR TOOLBAR
  // Arrow color Menu
  const arrowColorMenu = ref<WhiteboardSecondaryMenu | null>({
    id: 'color',
    items: arrowToolColors,
    defaultSelection: DEFAULT_COLORS.connector,
  })

  const arrowColorMenuSelectionId = ref<(typeof CONNECTOR_COLORS)[number]>(DEFAULT_COLORS.connector)

  function selectArrowColor(colorId: (typeof CONNECTOR_COLORS)[number]): void {
    arrowColorMenuSelectionId.value = colorId
  }

  const arrowColorTldrawToolId = computed<TldrawDefaultColorStyleColor | undefined>(
    () =>
      arrowColorMenu.value?.items.find((item) => item.id === arrowColorMenuSelectionId.value)?.tldrawToolId as
        | TldrawDefaultColorStyleColor
        | undefined,
  )

  // #endregion ARROW COLOR TOOLBAR

  /** GLOBAL ACTION */

  const canIUndo = ref(false)
  const canIRedo = ref(false)

  /**  */
  // #region Whiteboard shared state
  const isLoadingPortraitMobileLayout = computed(() => {
    if (useSurfaceContainerSizeStore().isLandscapePhone || useSurfaceContainerSizeStore().isLandscapeTablet) {
      return false
    }
    return useSurfaceContainerSizeStore().isContainerSmallerThanDesktop
  })

  // #endregion Embeds
  const whiteboardEmbedCodeUrlTemplate = computed(
    () => getVuexStore<RootState>()?.state.constants.whiteboardEmbedCodeUrlTemplate,
  )
  // #endregion

  // #endregion Fonts
  const defaultTldrawFontId = computed<TldrawFontId>(() => getTldrawFontId(surfaceStore.fontId) ?? 'draw')
  // #endregion

  return {
    /** FRAMES */
    // State
    frames,
    previousFrameId,
    currentFrameId,
    xFrameBackgroundPanel,
    xFrameSharePanel,
    xFrameShareViaAnotherAppPanel,
    frameBreakoutLinks,
    xFrameBreakoutLinksPanel,
    isFrameBreakout,
    breakoutFrameId,
    panelFrameId,
    hasReachedPlanFrameLimitAndCannotUpgrade,
    isLoadingPortraitMobileLayout,
    numUnapprovedShapes,
    unapprovedShapes,
    timesUnapprovedShapesUpdated,
    backgroundDominantColorInRgba,
    frameBarAndToolbarBackground,
    toolbarMenuBackground,
    frameBarHeight,
    setFrameBarHeight,

    // Actions
    isSingleFrame,
    isFirstFrame,
    isLastFrame,
    moveFrameUp,
    moveFrameDown,
    moveFrameAbove,
    moveFrameBelow,
    addFrameAbove,
    addFrameBelow,
    getFrameIndex,
    deleteFrame,
    copyLinkToFrame,
    copyFrameBreakoutLink,
    getLinkToFrame,
    getCanAddFrame,
    duplicateFrame,
    copyFrame,
    pasteFrame,
    deepCopyShapes,
    updateCopiedShapes,

    /** TOOLS */
    // State
    tools,
    toolStates,
    toolCurrentColors,
    selectedTool,
    selectedToolId,
    selectedSecondaryTool,
    ignoreTldrawTrigger,

    // Actions
    selectTool,
    selectDefaultTool,
    selectToolWithoutTriggeringTldraw,
    selectSecondaryTool,

    /** GEOMETRY COLOR TOOLBAR */
    // State
    geoColorMenu,
    geoColorMenuSelectionId,
    geoColorTldrawToolId,

    // Action
    selectGeoColor,

    /** ARROW COLOR TOOLBAR */
    // State
    arrowColorMenu,
    arrowColorMenuSelectionId,
    arrowColorTldrawToolId,

    // Action
    selectArrowColor,

    /** TOOLTIP */

    /** GLOBAL ACTION */
    canIUndo,
    canIRedo,

    /** EMBEDS */
    whiteboardEmbedCodeUrlTemplate,

    /** FONTS */
    defaultTldrawFontId,
  }
})
